import Link from 'next/link';
import type { IconType } from 'react-icons';
import {
  TbActivity,
  TbArrowRight,
  TbArrowsLeftRight,
  TbBuildingBank,
  TbBulb,
  TbCalculator,
  TbChartLine,
  TbFilter,
  TbGridDots,
  TbMail,
  TbSearch,
  TbTrendingDown,
  TbTrophy,
  TbUserSearch,
  TbVolume,
} from 'react-icons/tb';

type CalendarChip = {
  slug: string;
  label: string;
  href: string;
  aliases?: string[];
};

type LinkItem = {
  label: string;
  href: string;
  Icon?: IconType;
  external?: boolean;
};

type LinkColumn = {
  title: string;
  links: LinkItem[];
};

const CALENDAR_CHIPS: CalendarChip[] = [
  { slug: 'earnings', label: 'Earnings', href: '/earnings' },
  { slug: 'dividends', label: 'Dividends', href: '/dividends' },
  { slug: 'economic', label: 'Economic', href: '/calendars/economic' },
  { slug: 'ipos', label: 'IPOs', href: '/calendars/ipos' },
  { slug: 'fda', label: 'FDA', href: '/fda-calendar' },
  { slug: 'm-a', label: 'M&A', href: '/calendars/m-a' },
  { slug: 'stock-splits', label: 'Stock Splits', href: '/calendars/stock-splits' },
  { slug: 'guidance', label: 'Guidance', href: '/calendars/guidance' },
  { slug: 'spac', label: 'SPACs', href: '/calendars/spac' },
  { slug: 'conference-calls', label: 'Conference Calls', href: '/calendars/conference-calls' },
  { slug: 'events', label: 'Events', href: '/calendars/events' },
  {
    slug: 'analyst-ratings',
    label: 'Analyst Ratings',
    href: '/analyst-stock-ratings',
    aliases: ['analyst-predictions'],
  },
  { slug: 'unusual-options-activity', label: 'Unusual Options', href: '/calendars/unusual-options-activity' },
];

const LINK_COLUMNS: LinkColumn[] = [
  {
    title: 'Screeners & Research',
    links: [
      { label: 'Stock Screener', href: '/screener', Icon: TbFilter },
      { label: 'Top Stocks', href: '/screener/top-momentum-stocks', Icon: TbTrophy },
      { label: 'Compare Best Stocks', href: '/screener/best-momentum-stocks', Icon: TbArrowsLeftRight },
      { label: 'Trade Idea Feed', href: '/trading-ideas', Icon: TbBulb },
    ],
  },
  {
    title: 'Trades, Ratings & Markets',
    links: [
      { label: 'Markets', href: '/markets', Icon: TbChartLine },
      { label: 'Heatmap', href: '/tools/heatmap', Icon: TbGridDots },
      { label: 'Unusual Options', href: '/calendars/unusual-options-activity', Icon: TbActivity },
      { label: 'Short Interest', href: '/short-interest', Icon: TbTrendingDown },
      { label: 'Insider Trades', href: '/sec/insider-trades', Icon: TbUserSearch },
      { label: 'Government Trades', href: '/government-trades', Icon: TbBuildingBank },
    ],
  },
  {
    title: 'Calculators',
    links: [
      { label: 'Options Profit', href: '/tools/options-profit-calculator', Icon: TbCalculator },
      { label: 'Covered Call', href: '/tools/covered-call-calculator', Icon: TbCalculator },
      { label: 'Cash-Secured Put', href: '/tools/cash-secured-put-calculator', Icon: TbCalculator },
      { label: 'Long Call', href: '/tools/long-call-calculator', Icon: TbCalculator },
      { label: 'Long Put', href: '/tools/long-put-calculator', Icon: TbCalculator },
      { label: 'Margin', href: '/margin-calculator', Icon: TbCalculator },
      { label: 'Forex Profit', href: '/forex-profit-calculator', Icon: TbCalculator },
    ],
  },
  {
    title: 'Portfolios & Watchlists',
    links: [
      { label: 'Perfect Stock Portfolio', href: '/portfolio/perfect-stock-portfolio' },
      { label: 'Easy Income Portfolio', href: '/portfolio/easy-income-portfolio' },
      { label: 'Stock Whisper Index', href: '/portfolio/stock-whisper-index', Icon: TbVolume },
      { label: 'Free Newsletter', href: '/free-newsletters', Icon: TbMail },
      { label: 'Search', href: '/search', Icon: TbSearch },
    ],
  },
];
const PRO_CTA_HREF = 'https://www.benzinga.com/pro/register?utm_source=tools-footer';

const iconClass = 'h-[1em] w-[1em] shrink-0 align-[-0.15em]';

export interface ToolsFooterProps {
  currentCalendar?: string;
  className?: string;
}

const FooterLink = ({ label, href, Icon, external }: LinkItem) => {
  const content = (
    <>
      {Icon && <Icon aria-hidden="true" className={`${iconClass} text-bzgray-500 group-hover:text-[#3f83f8]`} />}
      {label}
    </>
  );

  const className =
    'group inline-flex items-center gap-1.5 text-[13px] text-bzgray-800 no-underline transition-colors hover:text-[#3f83f8]';

  if (external) {
    return (
      <a className={className} href={href} rel="noopener noreferrer" target="_blank">
        {content}
      </a>
    );
  }

  return (
    <Link className={className} href={href}>
      {content}
    </Link>
  );
};

export const ToolsFooter = ({ currentCalendar, className }: ToolsFooterProps) => {
  return (
    <footer
      className={`mt-8 border-t border-bzblue-300 bg-[#f1f4f9] px-6 py-9 text-bzgray-800${className ? ` ${className}` : ''}`}
    >
      <div className="mx-auto max-w-[1200px]">
        <div className="mb-7">
          <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-bzgray-600">
            {currentCalendar ? 'Other calendars' : 'Calendars'}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {CALENDAR_CHIPS.map(chip => {
              const isCurrent =
                chip.slug === currentCalendar ||
                (!!currentCalendar && (chip.aliases?.includes(currentCalendar) ?? false));
              return (
                <Link
                  key={chip.slug}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={`rounded-full border px-3 py-1.5 text-[13px] no-underline transition-colors ${
                    isCurrent
                      ? 'border-[#3f83f8] bg-[#3f83f8] font-medium text-white'
                      : 'border-bzblue-300 bg-white text-bzgray-800 hover:border-[#3f83f8] hover:bg-[#3f83f8] hover:text-white'
                  }`}
                  href={chip.href}
                >
                  {chip.label}
                </Link>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-6 border-t border-bzblue-300 pt-6">
          {LINK_COLUMNS.map(col => (
            <div key={col.title}>
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-bzgray-600">{col.title}</div>
              <ul className="m-0 list-none p-0">
                {col.links.map(link => (
                  <li key={link.label} className="mb-2">
                    <FooterLink {...link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-7 flex flex-wrap items-center justify-between gap-4 border-t border-bzblue-300 pt-5">
          <Link
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#3f83f8] no-underline hover:text-bzblue-800 hover:underline"
            href="/tools"
          >
            Browse all Benzinga tools
            <TbArrowRight aria-hidden="true" className={iconClass} />
          </Link>
          <div className="text-[13px] text-bzgray-600">
            Want real-time alerts on every event?{' '}
            <a
              className="font-medium text-[#3f83f8] no-underline hover:underline"
              href={PRO_CTA_HREF}
              rel="noopener noreferrer"
              target="_blank"
            >
              Try Benzinga Pro free →
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
